import React from "react";
import MultiStepLayout from "../components/MultiStepLayout";
import Input from "../components/Input";
import PhoneNumberInput2 from "../components/PhoneNumberInput2";
import Dropdown from "../components/Dropdown";
import Button from "../components/Button";
import Textarea from "../components/Textarea";
import PoweredBy from "../components/PoweredBy";
import { useTranslation } from "react-i18next";
import AutoComplete from "../components/AutoComplete";
import ReCAPTCHA from "react-google-recaptcha-enterprise";
import { createPortal } from 'react-dom';

function Step1({
  leadForm,
  lead,
  onChange,
  onBlur,
  submitStep1,
  submitting,
  countryCode,
  isMobile,
  geoLocation,
  activeElement,
  setActiveElement,
}) {
  const { t } = useTranslation();

  const bodyClick = (e) => {
    if (e.target.nodeName === "DIV") {
      setActiveElement(null);
    }
  }

  const recaptchaRef = React.createRef();

  const handleSubmit = () => {
    submitStep1(() => recaptchaRef.current.execute());
  };

  return (
    <MultiStepLayout
      backgroundColor={leadForm.backgroundColor}
      foregroundColor={leadForm.titleColor}
      formBackgroundColor={leadForm.formBackgroundColor}
      isMobile={isMobile}
      className="py-6"
    >
      <div className="text-2xl font-bold mx-0 mb-[16px] text-center" onClick={(e) => bodyClick(e)}>
        {leadForm.title}
      </div>
      {createPortal(
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
        />, document.body
      )}
      <div className={`bodyClick flex gap-[16px] ${isMobile ? "flex-col" : "flex-row"}`} onClick={(e) => bodyClick(e)}>
        <div className={`flex flex-col gap-[16px] ${isMobile ? "w-1/1" : "w-1/2"}`}>
          <Input
            label={t("your_name")}
            error={lead.errors.fullName}
            field_name="fullName"
            value={lead.fullName}
            required={true}
            onChange={onChange}
            onBlur={onBlur}
            type="text"
            borderColor={leadForm.inputBorderColor}
            setActiveElement={setActiveElement}
          />
          <Input
            label={t("email")}
            field_name="email"
            error={lead.errors.email}
            value={lead.email}
            required={true}
            onChange={onChange}
            onBlur={onBlur}
            type="text"
            borderColor={leadForm.inputBorderColor}
            setActiveElement={setActiveElement}
          />
          <PhoneNumberInput2
            label={t("phone_number")}
            field_name="phoneNumber"
            countryCode={countryCode}
            error={lead.errors.phoneNumber}
            value={lead.phoneNumber}
            required={true}
            onBlur={onBlur}
            onChange={onChange}
            borderColor={leadForm.inputBorderColor}
            geoLocation={geoLocation}
            setActiveElement={setActiveElement}
          />
          {!isMobile && <Dropdown
            position="auto"
            label={t("preferred_language")}
            field_name="language"
            error={lead.errors.language}
            value={lead.language}
            required={true}
            onChange={onChange}
            onBlur={onBlur}
            values={leadForm.languages}
            borderColor={leadForm.inputBorderColor}
            activeElement={activeElement}
            setActiveElement={setActiveElement}
          />}
          <div className={`mt-auto mr-auto ${isMobile ? "hidden" : "block"}`}>
            <PoweredBy backgroundColor={leadForm.formBackgroundColor} />
          </div>
        </div>
        <div className={`flex flex-col gap-[16px] ${isMobile ? "w-1/1" : "w-1/2"}`}>
          <AutoComplete
            position="auto"
            label={t("primary_treatment")}
            error={lead.errors.procedure}
            field_name="procedure"
            value={lead.procedure}
            required={true}
            onChange={onChange}
            onBlur={onBlur}
            values={leadForm.procedures}
            borderColor={leadForm.inputBorderColor}
            activeElement={activeElement}
            setActiveElement={setActiveElement}
          />
          {isMobile && <Dropdown
            position="auto"
            label={t("preferred_language")}
            field_name="language"
            error={lead.errors.language}
            value={lead.language}
            required={true}
            onChange={onChange}
            onBlur={onBlur}
            values={leadForm.languages}
            borderColor={leadForm.inputBorderColor}
            activeElement={activeElement}
            setActiveElement={setActiveElement}
          />}
          <Textarea
            label={t("additional_information")}
            field_name="notes"
            value={lead.notes}
            required={false}
            onChange={onChange}
            onBlur={onBlur}
            borderColor={leadForm.inputBorderColor}
            setActiveElement={setActiveElement}
          />

          <div className={`relative flex flex-col gap-2 pt-[16px] pr-[14px] pb-[20px] pl-[14px] rounded-lg border border-${leadForm.inputBorderColor} bg-white`}>
            <div className="text-gray-400 font-normal absolute top-[-0.5rem] left-4 bg-white text-xs px-[6px] py-[2px] rounded-lg">
              {t("how_should_we_connect")}<sup>*</sup>
            </div>
            <div className="flex gap-[16px] justify-between">
              <label className="flex items-center gap-[10px] cursor-pointer select-none">
                <input
                  type="radio"
                  name="messageChannel"
                  value="sms"
                  checked={lead.messageChannel === 'sms'}
                  onChange={() => onChange('messageChannel', 'sms')}
                  style={{ accentColor: lead.messageChannel === 'sms' ? '#333333' : '#9CA3AF' }}
                />
                <span className={`text-sm ${lead.messageChannel === 'sms' ? 'font-medium text-[#333333]' : 'font-normal text-gray-400'}`}>
                  {t("text")}
                </span>
              </label>
              <label className="flex items-center gap-[10px] cursor-pointer select-none">
                <input
                  type="radio"
                  name="messageChannel"
                  value="whatsapp"
                  checked={lead.messageChannel === 'whatsapp'}
                  onChange={() => onChange('messageChannel', 'whatsapp')}
                  style={{ accentColor: lead.messageChannel === 'whatsapp' ? '#333333' : '#9CA3AF' }}
                />
                <span className={`text-sm ${lead.messageChannel === 'whatsapp' ? 'font-medium text-[#333333]' : 'font-normal text-gray-400'}`}>
                  {t("whatsapp")}
                </span>
              </label>
            </div>
            {lead.messageChannel && (
              <label className="flex items-start gap-2 cursor-pointer select-none mt-1">
                <input
                  type="checkbox"
                  checked={!!lead.messageChannelConsent}
                  onChange={(e) => onChange('messageChannelConsent', e.target.checked)}
                  className="mt-0.5 flex-shrink-0"
                  style={{ accentColor: '#0000008A' }}
                />
                <span className="text-xs text-[#333333] font-normal">
                  {lead.messageChannel === 'sms' ? t("text_consent") : t("whatsapp_consent")}
                </span>
              </label>
            )}
          {(lead.errors.messageChannel || lead.errors.messageChannelConsent) && (
          <div className="text-red-600 ml-0 text-xs px-2 -bottom-3 bg-white rounded absolute">
            {lead.errors.messageChannel || lead.errors.messageChannelConsent}
          </div>
          )}
          </div>

          <Button
            title={leadForm.submitText}
            backgroundColor={leadForm.submitBackgroundColor}
            color={leadForm.submitColor}
            active={!submitting}
            onClick={handleSubmit}
          />
        </div>

        <div
          className={`w-full flex justify-center ${isMobile ? "" : "hidden"}`}
        >
          <PoweredBy backgroundColor={leadForm.formBackgroundColor} />
        </div>
      </div>
    </MultiStepLayout>
  );
}

export default Step1;
